import { HttpClient, APIResponseType } from './types';
import { AxiosAdapter } from './AxiosAdapter'; // or FetchAdapter
import { QueryClient, isCancelledError } from '@tanstack/react-query';
import queryClient from './queryClient';
import { header, handleLogout } from 'utils/auth';
import { appendAgentTypeToUrl } from 'app/utils/agentType';
import { ensureCsrfToken } from 'utils/csrf';
// import { notifyForbiddenError } from 'utils/notifyForbiddenError';

class ApiService {
  private httpClient: HttpClient;
  private queryClient: QueryClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
    this.queryClient = queryClient;
  }

  // Helper method to generate query key
  private getQueryKey(method: string, url: string, params?: Record<string, any>): any[] {
    return [method, url, params ? { ...params } : {}];
  }

  /**
   * Awaits a React-Query `fetchQuery` promise and translates a `CancelledError`
   * (raised when our own `invalidateCacheByPrefix` or any other `removeQueries`
   * call drops an in-flight query) into a typed Error.
   *
   * Two things happen here:
   *  - We pre-attach a no-op `.catch` on the raw fetchQuery promise so the
   *    rejection is considered handled at the unhandled-promise-rejection
   *    level (this is what keeps the CRA dev overlay from showing
   *    `[object Object]` for these silent cancellations).
   *  - The separate `await` still throws so the caller's `try/catch` works,
   *    and we wrap the cancellation in a proper `Error` with an `isCancelled`
   *    flag so consumers can recognise it without re-importing
   *    `isCancelledError` everywhere.
   */
  private async awaitWithCancellationGuard<T>(
    promise: Promise<APIResponseType<T>>,
  ): Promise<APIResponseType<T>> {
    promise.catch(() => {});
    try {
      return await promise;
    } catch (error) {
      if (isCancelledError(error) || (error as any)?.silent === true) {
        const cancelled = new Error('Request cancelled') as Error & {
          isCancelled?: boolean;
          silent?: boolean;
        };
        cancelled.isCancelled = true;
        cancelled.silent = true;
        throw cancelled;
      }
      throw error;
    }
  }

  // Generic method to make GET requests with caching
  async get<T>(url: string, params?: Record<string, any>): Promise<APIResponseType<T>> {
    // Check if this is a blob request and disable caching
    if (params?.responseType === 'blob') {
      return this.httpClient.get<T>(url, params);
    }

    const queryKey = this.getQueryKey('GET', url, params);

    // Return cached data if available, otherwise fetch fresh data
    return this.awaitWithCancellationGuard(
      this.queryClient.fetchQuery({
        queryKey,
        queryFn: () => this.httpClient.get<T>(url, params),
      }),
    );
  }

  // Generic method to make POST requests (with optional caching)
  async post<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    console.log("ApiService post method called with URL:", url);
    console.log("POST data:", data);

    if (enableCache) {
      const queryKey = this.getQueryKey('POST', url, { data, ...config });
      return this.awaitWithCancellationGuard(
        this.queryClient.fetchQuery({
          queryKey,
          queryFn: () => {
            console.log("Executing POST request via queryFn");
            return this.httpClient.post<T>(url, data, config);
          },
        }),
      );
    }
    return this.httpClient.post<T>(url, data, config);
  }

  // Generic method to make PUT requests (with optional caching)
  async put<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    if (enableCache) {
      const queryKey = this.getQueryKey('PUT', url, { data, ...config });
      return this.awaitWithCancellationGuard(
        this.queryClient.fetchQuery({
          queryKey,
          queryFn: () => this.httpClient.put<T>(url, data, config),
        }),
      );
    }
    return this.httpClient.put<T>(url, data, config);
  }

  // Generic method to make DELETE requests
  async delete<T>(url: string, config?: any): Promise<APIResponseType<T>> {
    // Typically we don't cache DELETE requests
    return this.httpClient.delete<T>(url, config);
  }

  /**
   * Marks the cached entry for a specific endpoint as stale and refetches any
   * active observers. We deliberately use `invalidateQueries` rather than
   * `removeQueries` so in-flight requests are *not* aborted — aborting them
   * raises a React-Query `CancelledError` that bubbles up as an unhandled
   * promise rejection (showing `[object Object]` in the CRA dev overlay).
   */
  invalidateCache(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url, params);

    console.log("🔄 Invalidating Cache for:", queryKey);

    void this.queryClient.invalidateQueries({ queryKey });
  }

  /**
   * Same intent as {@link invalidateCache} but matches all queries whose
   * key *starts with* the given (`method`, `url`) tuple. Use this when you
   * want to refresh a whole family of endpoints (e.g. all `api/v2/leads…`
   * variants) without aborting any of their in-flight calls.
   */
  invalidateCacheByPrefix(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url);
    console.log("🔄 Invalidating Cache by prefix for:", queryKey);
    void this.queryClient.invalidateQueries({
      queryKey,
      exact: false, // important for partial match
    });
  }

  /**
   * Stream text response from server using Server-Sent Events or chunked response
   * @param url - API endpoint (relative to base URL)
   * @param body - Request body to send
   * @param onChunk - Callback function called for each chunk of data received
   * @param options - Optional configuration (headers, etc.)
   * @returns Promise that resolves when streaming is complete
   */
  async streamText(
    url: string,
    body: any,
    onChunk: (text: string) => void,
    options?: {
      headers?: Record<string, string>;
      onError?: (error: Error) => void;
      onComplete?: () => void;
    }
  ): Promise<void> {
    const baseURL = process.env.REACT_APP_API_URL || '';
    const fullUrl = appendAgentTypeToUrl(
      url.startsWith('http') ? url : `${baseURL}${url}`,
    );

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    const authHeader = header();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    try {
      const csrf = await ensureCsrfToken();
      if (csrf) {
        headers['X-CSRF-Token'] = csrf;
      }
    } catch (e) {
      console.warn('streamText CSRF', e);
    }

    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(body),
      });

      // Force logout on unauthorized response.
      if (response.status === 401) {
        handleLogout();
        throw new Error('Access Denied. jwt expired');
      }
      // if (response.status === 403) {
      //   notifyForbiddenError({ statusCode: 403 }, { method: 'POST', url });
      // }

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Check if response body exists
      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          onChunk(chunk);
        }
      } finally {
        reader.releaseLock();
      }

      // Call completion callback if provided
      if (options?.onComplete) {
        options.onComplete();
      }
    } catch (error: any) {
      console.error('Error in streamText:', error);
      
      // Call error callback if provided
      if (options?.onError) {
        options.onError(error instanceof Error ? error : new Error(String(error)));
      } else {
        // Re-throw if no error handler provided
        throw error;
      }
    }
  }

  // Method to clear entire cache
  // clearCache() {
  //   this.queryClient.clear();
  // }
}

// Create an instance of ApiService with the desired HTTP client
const httpClient = new AxiosAdapter(); // or new FetchAdapter()
const apiService = new ApiService(httpClient);

// Add S3 content fetching service
export const fetchS3ContentThroughBackend = async (s3Url: string): Promise<{ content: string; contentType?: string; }> => {
  try {
    const response = await httpClient.post<{ content: string; contentType?: string; }>('api/fetch-s3-content', {
      url: s3Url,
    });

    if (response.body) {
      return response.body;
    } else {
      throw new Error(response.message || 'Failed to fetch S3 content');
    }
  } catch (error) {
    console.error('Error fetching S3 content through backend:', error);
    throw error;
  }
};

export default apiService;

// Export all API services
export { default as DealerContentSettings } from './dealerContentSettings/dealerContentSettingsApiService';
export { default as MiaChatV2 } from './miaChat/miaChatV2ApiService';
export { default as EmailTemplateApiService } from './emailTemplate/emailTemplateApiService';
export { default as ImportExportApiService } from './importExport/importExportApiService';